import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, Pencil, Plus, Tag, Trash2, UserRoundX, Users } from "lucide-react";
import type { ContactGroupSummary } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import {
  workspaceInternalNavItemClass,
  workspaceInternalNavLabelClass,
} from "../../../components/layout/workspace-internal-sidebar.js";
import { contactGroupChipClassName } from "../contact-group-color.js";

function drawerNavItemClass(selected: boolean): string {
  return cn(
    "group flex min-h-11 w-full min-w-0 items-center gap-3 rounded-lg border-s-2 px-3 py-2.5 text-start transition-colors duration-150 motion-reduce:transition-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/40",
    selected
      ? "border-accent bg-surface-selected font-semibold text-foreground"
      : "border-transparent font-medium text-foreground/80 hover:bg-surface-hover",
  );
}

function drawerIconWellClass(selected: boolean): string {
  return cn(
    "inline-grid size-8 shrink-0 place-items-center rounded-lg",
    selected ? "bg-accent-soft text-accent" : "bg-surface-muted text-muted group-hover:text-foreground",
  );
}

export function ContactsGroupsPanel({
  groups,
  selectedGroupId,
  groupFormOpen,
  newGroupName,
  collapsed = false,
  onNewGroupNameChange,
  onOpenGroupForm,
  onCloseGroupForm,
  onCreateGroup,
  onSelectAllContacts,
  onSelectGroup,
  onRenameGroup,
  onDeleteGroup,
  onDeleteAllContacts,
  onEnsureExpanded,
  className,
  variant = "rail",
  allContactsCount,
}: {
  groups: ContactGroupSummary[];
  selectedGroupId: number | null;
  groupFormOpen: boolean;
  newGroupName: string;
  collapsed?: boolean;
  onNewGroupNameChange: (value: string) => void;
  onOpenGroupForm: () => void;
  onCloseGroupForm: () => void;
  onCreateGroup: () => void | Promise<void>;
  onSelectAllContacts: () => void;
  onSelectGroup: (groupId: number) => void;
  onRenameGroup: (groupId: number, name: string) => Promise<void>;
  onDeleteGroup: (groupId: number) => Promise<void>;
  onDeleteAllContacts: (group: ContactGroupSummary) => void;
  /** Desktop collapsed rail: expand before inline create/rename UI that needs labels. */
  onEnsureExpanded?: () => void;
  className?: string;
  variant?: "rail" | "drawer";
  allContactsCount?: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;
  const allLabel = t("contacts.sidebar.allContacts");

  const allButton = (
    <button
      type="button"
      className={
        isDrawer
          ? drawerNavItemClass(selectedGroupId === null)
          : workspaceInternalNavItemClass(selectedGroupId === null, collapsed)
      }
      onClick={onSelectAllContacts}
      aria-label={allLabel}
      aria-current={selectedGroupId === null ? "page" : undefined}
      data-testid="contacts-sidebar-all"
    >
      {isDrawer ? (
        <span className={drawerIconWellClass(selectedGroupId === null)}>
          <Users size={16} aria-hidden="true" />
        </span>
      ) : (
        <Users
          size={16}
          className={cn("shrink-0", selectedGroupId === null ? "text-accent" : "text-muted")}
          aria-hidden="true"
        />
      )}
      <span className={isDrawer ? "min-w-0 flex-1 text-[0.875rem] leading-5" : workspaceInternalNavLabelClass(showLabels)}>
        {allLabel}
      </span>
      {isDrawer && allContactsCount != null ? (
        <span
          className={cn(
            "ms-auto inline-flex min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-[0.8125rem] font-bold tabular-nums leading-5",
            selectedGroupId === null ? "bg-accent-soft text-accent" : "bg-surface-muted text-muted",
          )}
          aria-hidden="true"
        >
          {allContactsCount}
        </span>
      ) : null}
    </button>
  );

  const addGroupButton = (
    <button
      type="button"
      className={cn(
        "inline-grid size-7 shrink-0 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-foreground",
        collapsed && "size-8",
      )}
      aria-label={t("contacts.sidebar.addGroup")}
      onClick={onOpenGroupForm}
      data-testid="contacts-sidebar-add-group"
    >
      <Plus size={14} aria-hidden="true" />
    </button>
  );

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <nav
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          isDrawer ? "px-0 py-0" : "px-1.5 py-2",
          collapsed && !isDrawer && "px-1",
        )}
        aria-label={t("contacts.sidebar.title")}
      >
        {collapsed ? (
          <SimpleTooltip content={allLabel}>
            <div className="w-full">{allButton}</div>
          </SimpleTooltip>
        ) : (
          allButton
        )}

        <div
          className={cn(
            isDrawer ? "mt-4 mb-2 flex items-center" : "mt-3 mb-1 flex items-center",
            collapsed ? "justify-center" : "justify-between px-2",
          )}
        >
          {showLabels ? (
            <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.04em] text-ink-subtle">
              {t("contacts.sidebar.groups")}
            </span>
          ) : null}
          {collapsed ? (
            <SimpleTooltip content={t("contacts.sidebar.addGroup")}>
              <div>{addGroupButton}</div>
            </SimpleTooltip>
          ) : (
            addGroupButton
          )}
        </div>

        {groupFormOpen && showLabels ? (
          <div className="mb-2 space-y-2 px-2">
            <input
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[0.8125rem]"
              value={newGroupName}
              onChange={(event) => onNewGroupNameChange(event.target.value)}
              placeholder={t("contacts.sidebar.groupNamePlaceholder")}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void onCreateGroup()}>
                {t("common.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={onCloseGroupForm}>
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        ) : null}

        <ul className={cn("m-0 grid list-none p-0", isDrawer ? "gap-1.5" : "gap-0.5")}>
          {groups.map((group) => (
            <li key={group.id}>
              <ContactsGroupListItem
                group={group}
                active={selectedGroupId === group.id}
                collapsed={collapsed}
                variant={variant}
                onSelect={() => onSelectGroup(group.id)}
                onRename={(name) => onRenameGroup(group.id, name)}
                onDelete={() => onDeleteGroup(group.id)}
                onDeleteAllContacts={() => onDeleteAllContacts(group)}
                onEnsureExpanded={onEnsureExpanded}
              />
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function ContactsGroupListItem({
  group,
  active,
  collapsed,
  variant = "rail",
  onSelect,
  onRename,
  onDelete,
  onDeleteAllContacts,
  onEnsureExpanded,
}: {
  group: ContactGroupSummary;
  active: boolean;
  collapsed: boolean;
  variant?: "rail" | "drawer";
  onSelect: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onDeleteAllContacts: () => void;
  onEnsureExpanded?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDrawer = variant === "drawer";
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [menuOpen, setMenuOpen] = useState(false);

  if (editing && !collapsed) {
    return (
      <div className="space-y-1 px-2 py-1">
        <input
          className="w-full rounded border border-border px-2 py-1 text-[0.8125rem]"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void onRename(name);
              setEditing(false);
            }}
          >
            {t("common.save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  const selectButton = (
    <button
      type="button"
      className={isDrawer ? drawerNavItemClass(active) : workspaceInternalNavItemClass(active, collapsed)}
      onClick={onSelect}
      aria-label={group.name}
      aria-current={active ? "page" : undefined}
    >
      {isDrawer ? (
        <span className={drawerIconWellClass(active)}>
          <Tag size={16} aria-hidden="true" />
        </span>
      ) : (
        <Tag
          size={16}
          className={cn("shrink-0", active ? "text-accent" : "text-muted")}
          aria-hidden="true"
        />
      )}
      {isDrawer ? (
        <span
          className={cn(
            contactGroupChipClassName(group.id),
            "max-w-[12rem] px-2.5 py-1 text-[0.75rem] font-semibold",
          )}
        >
          <span className="truncate">{group.name}</span>
        </span>
      ) : (
        <span className={workspaceInternalNavLabelClass(!collapsed)}>{group.name}</span>
      )}
      {isDrawer ? (
        <span
          className={cn(
            "ms-auto inline-flex min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-[0.8125rem] font-bold tabular-nums leading-5",
            active ? "bg-accent-soft text-accent" : "bg-surface-muted text-muted",
          )}
          aria-hidden="true"
        >
          {group.memberCount}
        </span>
      ) : null}
    </button>
  );

  const actionsMenu = (
    <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-grid shrink-0 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-foreground",
            collapsed
              ? cn(
                  "absolute end-0 top-0 size-4 bg-surface/95 shadow-sm",
                  "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                  (active || menuOpen) && "opacity-100",
                )
              : cn(
                  "size-7",
                  // Touch: always visible. Fine pointer: reveal on row hover / open.
                  "opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:focus-visible:opacity-100 data-[state=open]:opacity-100",
                ),
          )}
          aria-label={t("contacts.row.actions", { name: group.name })}
          data-testid={`contact-group-menu-${group.id}`}
          onClick={(event) => {
            // Keep group selection independent from opening actions.
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
        >
          <MoreHorizontal size={collapsed ? 10 : 14} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={12}
        className="z-[var(--z-popover)] min-w-44 p-1"
      >
        <DropdownMenuItem
          className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium"
          onSelect={() => {
            onEnsureExpanded?.();
            setName(group.name);
            setEditing(true);
          }}
        >
          <Pencil size={14} className="shrink-0 text-muted" aria-hidden="true" />
          {t("contacts.sidebar.renameGroup")}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium"
          disabled={group.memberCount <= 0}
          onSelect={() => onDeleteAllContacts()}
          data-testid={`contact-group-delete-contacts-${group.id}`}
        >
          <UserRoundX size={14} className="shrink-0 text-muted" aria-hidden="true" />
          {t("contacts.bulk.deleteAllInGroup")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium text-destructive data-[highlighted]:bg-error-muted"
          onSelect={() => void onDelete()}
          data-testid={`contact-group-delete-${group.id}`}
        >
          <Trash2 size={14} className="shrink-0" aria-hidden="true" />
          {t("contacts.sidebar.deleteGroup")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (collapsed) {
    return (
      <div className="group relative w-full">
        <SimpleTooltip content={group.name}>
          <div className="w-full">{selectButton}</div>
        </SimpleTooltip>
        {actionsMenu}
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1">
      {selectButton}
      {actionsMenu}
    </div>
  );
}
